import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, Edit2, Check, X,
  ShieldCheck, ShieldX, Wand2, FileText, RotateCcw
} from 'lucide-react';
import axios from 'axios';
import { DocumentExportActions } from './DocumentExportActions';
import { recalculateLineItem } from './lineItemCalculations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  Open: 'bg-blue-100 text-blue-700',
  Closed: 'bg-green-100 text-green-700',
};
const QC_STYLES = {
  pending: 'bg-slate-100 text-slate-500',
  passed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

function generateItemId(productCode) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const rand = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `${(productCode || 'ITEM').toUpperCase()}-${rand}`;
}

export const InwardDeliveryChallanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);

  // qcData: { [liIdx]: [{unit_number, item_id, asset_id, qc_status, remark}] }
  const [qcData, setQcData] = useState({});
  const [qcDirty, setQcDirty] = useState(false);
  const [savingQC, setSavingQC] = useState(false);

  // QC modal state
  const [qcModal, setQcModal] = useState(null); // { liIdx, unitIdx }
  const [qcModalStatus, setQcModalStatus] = useState('passed');
  const [qcModalRemark, setQcModalRemark] = useState('');
  const [qcModalError, setQcModalError] = useState('');

  // Status
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);

  // Convert modal (Purchase Bill)
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [modalItems, setModalItems] = useState([]);

  // Return modal
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returningUnits, setReturningUnits] = useState([]);
  const [creatingReturn, setCreatingReturn] = useState(false);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const initQCFromDoc = useCallback((docData) => {
    const initialQC = {};
    (docData.line_items || []).forEach((li, idx) => {
      const qty = Math.max(1, Math.floor(li.quantity) || 1);
      const existing = li.qc_units || [];
      if (existing.length === qty) {
        initialQC[idx] = existing.map(u => ({ ...u, remark: u.remark || '' }));
      } else {
        initialQC[idx] = Array.from({ length: qty }, (_, i) => {
          const found = existing.find(u => u.unit_number === i + 1);
          return found
            ? { ...found, remark: found.remark || '' }
            : { unit_number: i + 1, item_id: '', asset_id: '', qc_status: 'pending', remark: '' };
        });
      }
    });
    setQcData(initialQC);
    setQcDirty(false);
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [docRes, prodRes, unitRes, taxRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/inward-delivery-challans/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
        ]);
        setDoc(docRes.data);
        setProducts(prodRes.data);
        setUnits(unitRes.data);
        setTaxes(taxRes.data);
        initQCFromDoc(docRes.data);
      } catch {
        setError('Failed to load inward delivery challan');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getName = (list, lid, field = 'name') => list.find(i => i.id === lid)?.[field] || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  // ── QC helpers ─────────────────────────────────────────────────────────────
  const updateQCUnit = (liIdx, unitIdx, field, value) => {
    setQcData(prev => {
      const list = [...(prev[liIdx] || [])];
      list[unitIdx] = { ...list[unitIdx], [field]: value };
      return { ...prev, [liIdx]: list };
    });
    setQcDirty(true);
  };

  const autoGenItemId = (liIdx, unitIdx) => {
    const li = doc?.line_items?.[liIdx];
    const prod = products.find(p => p.id === li?.product_id);
    const newId = generateItemId(prod?.product_code || prod?.name?.slice(0, 4));
    updateQCUnit(liIdx, unitIdx, 'item_id', newId);
  };

  const autoGenAllItemIds = () => {
    setQcData(prev => {
      const generated = new Set(Object.values(prev).flat().map(unit => unit.item_id).filter(Boolean));
      const next = {};
      Object.entries(prev).forEach(([liIdx, unitsForLine]) => {
        const li = doc?.line_items?.[Number(liIdx)];
        const product = products.find(p => p.id === li?.product_id);
        next[liIdx] = unitsForLine.map(unit => {
          if (unit.item_id?.trim()) return unit;
          let itemId;
          do {
            itemId = generateItemId(product?.product_code || product?.name?.slice(0, 4));
          } while (generated.has(itemId));
          generated.add(itemId);
          return { ...unit, item_id: itemId };
        });
      });
      return next;
    });
    setQcDirty(true);
  };

  // Open QC modal for a unit
  const openQCModal = (liIdx, unitIdx) => {
    const unit = qcData[liIdx]?.[unitIdx];
    if (!unit?.item_id?.trim()) {
      setError('Please enter an Item ID before marking QC status.');
      return;
    }
    setError('');
    setQcModalStatus(unit.qc_status === 'pending' ? 'passed' : unit.qc_status);
    setQcModalRemark(unit.remark || '');
    setQcModalError('');
    setQcModal({ liIdx, unitIdx });
  };

  const saveQCModal = () => {
    if (qcModalStatus === 'failed' && !qcModalRemark.trim()) {
      setQcModalError('Remark is required when marking as Failed.');
      return;
    }
    const { liIdx, unitIdx } = qcModal;
    setQcData(prev => {
      const list = [...(prev[liIdx] || [])];
      list[unitIdx] = { ...list[unitIdx], qc_status: qcModalStatus, remark: qcModalRemark.trim() };
      return { ...prev, [liIdx]: list };
    });
    setQcDirty(true);
    setQcModal(null);
  };

  // Reset a unit's QC
  const resetQCUnit = (liIdx, unitIdx) => {
    setQcData(prev => {
      const list = [...(prev[liIdx] || [])];
      list[unitIdx] = { ...list[unitIdx], qc_status: 'pending', remark: '' };
      return { ...prev, [liIdx]: list };
    });
    setQcDirty(true);
  };

  const hasPassedUnits = Object.values(qcData).some(us => us.some(u => u.qc_status === 'passed'));
  const hasFailedUnits = Object.values(qcData).some(us => us.some(u => u.qc_status === 'failed'));
  const allUnitsQCCompleted = Object.values(qcData).length > 0 && Object.values(qcData).every(
    unitsForLine => unitsForLine.length > 0 && unitsForLine.every(unit => unit.qc_status === 'passed' || unit.qc_status === 'failed')
  );
  const allItemIdsGenerated = Object.values(qcData).length > 0 && Object.values(qcData).every(
    unitsForLine => unitsForLine.length > 0 && unitsForLine.every(unit => unit.item_id?.trim())
  );

  const saveQC = async () => {
    setSavingQC(true);
    setError('');
    try {
      const updatedLineItems = (doc.line_items || []).map((li, idx) => ({
        ...li,
        qc_units: qcData[idx] || [],
      }));
      const res = await axios.put(
        `${BACKEND_URL}/api/inward-delivery-challans/${id}`,
        { line_items: updatedLineItems },
        getAuthHeaders()
      );
      setDoc(res.data);
      initQCFromDoc(res.data);
    } catch {
      setError('Failed to save QC data');
    } finally {
      setSavingQC(false);
    }
  };

  // ── Status helpers ──────────────────────────────────────────────────────────
  const handleMarkClosed = async () => {
    setUpdatingStatus(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/inward-delivery-challans/${id}`, { status: 'Closed' }, getAuthHeaders());
      setDoc(res.data);
      setShowStatusConfirm(false);
    } catch { setError('Failed to update status'); }
    finally { setUpdatingStatus(false); }
  };

  const handleReopen = async () => {
    setUpdatingStatus(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/inward-delivery-challans/${id}`, { status: 'Open' }, getAuthHeaders());
      setDoc(res.data);
    } catch { setError('Failed to update status'); }
    finally { setUpdatingStatus(false); }
  };

  // ── Convert to Purchase Bill modal ──────────────────────────────────────────
  const openConvertModal = () => {
    const byProduct = {};
    (doc.line_items || []).forEach((li, idx) => {
      if (!li.product_id) return;
      const passed = (qcData[idx] || []).filter(u => u.qc_status === 'passed');
      if (!passed.length) return;
      if (!byProduct[li.product_id]) {
        byProduct[li.product_id] = {
          key: Date.now() + Math.random(),
          product_id: li.product_id,
          description: li.description || '',
          unit_id: li.unit_id || '',
          tax_id: li.tax_id || '',
          price_per_unit: li.price_per_unit || 0,
          discount_percentage: li.discount_percentage || 0,
          discount_amount: 0, tax_amount: 0, total_amount: 0,
          mrp: li.mrp || '',
          warranty_id: li.warranty_id || '',
          warranty_name: li.warranty_name || '',
          quantity: 0, item_ids: [], asset_ids: [],
        };
      }
      byProduct[li.product_id].quantity += passed.length;
      byProduct[li.product_id].item_ids.push(...passed.map(u => u.item_id || ''));
      byProduct[li.product_id].asset_ids.push(...passed.map(u => u.asset_id || null));
    });
    const items = Object.values(byProduct).map(item => recalcModalItem(item));
    setModalItems(items);
    setShowConvertModal(true);
  };

  const recalcModalItem = (item, changedField = null) => recalculateLineItem(item, taxes, changedField);

  const updateModalItem = (idx, field, value) => {
    setModalItems(prev => {
      const updated = [...prev];
      updated[idx] = recalcModalItem({ ...updated[idx], [field]: value }, field);
      return updated;
    });
  };

  const handleConvert = () => {
    navigate('/admin/purchase/bills/new', {
      state: {
        fromIDC: {
          vendor_id: doc.vendor_id,
          company_id: doc.company_id,
          state_of_supply: doc.state_of_supply,
          source_idc_id: doc.id,
          source_idc_number: doc.challan_number,
          line_items: modalItems,
        },
      },
    });
  };

  // ── Return Failed Units ─────────────────────────────────────────────────────
  const openReturnModal = () => {
    const units = [];
    (doc.line_items || []).forEach((li, idx) => {
      if (!li.product_id) return;
      const failed = (qcData[idx] || []).filter(u => u.qc_status === 'failed');
      failed.forEach(u => {
        units.push({
          product_id: li.product_id,
          product_name: getName(products, li.product_id),
          unit_number: u.unit_number,
          item_id: u.item_id,
          remark: u.remark || '',
        });
      });
    });
    setReturningUnits(units);
    setShowReturnModal(true);
  };

  const handleCreateReturn = async () => {
    setCreatingReturn(true);
    setError('');
    try {
      await axios.post(
        `${BACKEND_URL}/api/return-inward-challans`,
        {
          source_idc_id: doc.id,
          source_idc_number: doc.challan_number,
          vendor_id: doc.vendor_id,
          company_id: doc.company_id,
          return_date: new Date().toISOString().slice(0, 10),
          returned_units: returningUnits,
        },
        getAuthHeaders()
      );
      // Refresh the IDC doc so return_created flag updates
      const res = await axios.get(`${BACKEND_URL}/api/inward-delivery-challans/${id}`, getAuthHeaders());
      setDoc(res.data);
      setShowReturnModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create Return Inward DC');
    } finally {
      setCreatingReturn(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error && !doc) return <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>;
  if (!doc) return null;

  const status = doc.status || 'Open';
  const isClosed = status === 'Closed';
  const billConverted = doc.bill_converted || false;
  const returnCreated = doc.return_created || false;
  const canCloseIDC = allUnitsQCCompleted && (!hasPassedUnits || billConverted) && (!hasFailedUnits || returnCreated);
  const modalSubtotal = modalItems.reduce((s, i) => s + i.total_amount, 0);

  // Show convert button only if there are passed units AND not yet converted
  const showConvertBtn = hasPassedUnits && allUnitsQCCompleted && !billConverted;
  // Show return button only if there are failed units AND not yet returned
  const showReturnBtn = hasFailedUnits && !returnCreated;

  const exportConfig = {
    title: 'Inward Delivery Challan', number: doc.challan_number,
    metadata: [
      { label: 'Challan Number', value: doc.challan_number || '-' }, { label: 'Challan Date', value: formatDate(doc.challan_date) },
      { label: 'Vendor', value: doc.vendor_name || '-' }, { label: 'Company (Receiving)', value: doc.company_name || '-' },
      { label: 'Due Date', value: formatDate(doc.due_date) }, { label: 'Status', value: status },
    ],
    columns: [
      { label: '#', value: (_, index) => index + 1, type: 'number' }, { label: 'Product', value: item => getName(products, item.product_id) },
      { label: 'Description', value: item => item.description || '-' }, { label: 'Qty', value: item => Number(item.quantity || 0), type: 'number' },
      { label: 'Unit', value: item => getName(units, item.unit_id) }, { label: 'Price/Unit', value: item => Number(item.price_per_unit || 0), type: 'number' },
      { label: 'Disc %', value: item => Number(item.discount_percentage || 0), type: 'number' }, { label: 'Tax', value: item => item.tax_id ? getName(taxes, item.tax_id) : '-' },
      { label: 'Tax Amt', value: item => Number(item.tax_amount || 0), type: 'number' }, { label: 'Amount', value: item => Number(item.total_amount || 0), type: 'number' },
      { label: 'QC Passed', value: item => (item.qc_units || []).filter(unit => unit.qc_status === 'passed').length, type: 'number' },
      { label: 'QC Failed', value: item => (item.qc_units || []).filter(unit => unit.qc_status === 'failed').length, type: 'number' },
    ],
    rows: doc.line_items || [],
    totals: [
      { label: 'Subtotal', value: `INR ${Number(doc.subtotal || 0).toFixed(2)}`, rawValue: Number(doc.subtotal || 0) },
      { label: 'Total', value: `INR ${Number(doc.total || 0).toFixed(2)}`, rawValue: Number(doc.total || 0) },
    ],
    shareLines: [`Vendor: ${doc.vendor_name || '-'}`, `Date: ${formatDate(doc.challan_date)}`, `Total: INR ${Number(doc.total || 0).toFixed(2)}`, `Status: ${status}`],
  };

  return (
    <div data-testid="inward-challan-detail-page">
      <button onClick={() => navigate('/admin/purchase/inward-challan')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors print:hidden">
        <ArrowLeft className="h-4 w-4" /> Back to Inward Delivery Challans
      </button>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Inward Delivery Challan</h1>
          <span className={`px-3 py-1 text-xs font-bold uppercase rounded ${STATUS_STYLES[status] || 'bg-slate-100 text-slate-700'}`}
            data-testid="status-badge">{status}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap print:hidden">
          {/* Convert to Purchase Bill — hidden once converted */}
          {showConvertBtn && (
            <button onClick={openConvertModal} data-testid="convert-to-bill-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-orange-600 transition-colors">
              <FileText className="h-3.5 w-3.5" /> Convert to Purchase Bill
            </button>
          )}
          {billConverted && (
            <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-50 border border-green-200 text-green-700 text-xs font-semibold rounded-sm">
              <Check className="h-3.5 w-3.5" /> Bill Created
            </span>
          )}
          {/* Return Failed Units — hidden once returned */}
          {showReturnBtn && (
            <button onClick={openReturnModal} data-testid="return-failed-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-red-700 transition-colors">
              <RotateCcw className="h-3.5 w-3.5" /> Return Failed Units
            </button>
          )}
          {returnCreated && (
            <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-sm">
              <RotateCcw className="h-3.5 w-3.5" /> Return Created
            </span>
          )}
          {/* Status toggle */}
          {isClosed ? (
            <button onClick={handleReopen} disabled={updatingStatus} data-testid="reopen-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-600 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-slate-700 transition-colors disabled:opacity-50">
              <Edit2 className="h-3.5 w-3.5" /> Reopen
            </button>
          ) : canCloseIDC ? (
            <button onClick={() => setShowStatusConfirm(true)} disabled={updatingStatus} data-testid="mark-closed-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-green-700 transition-colors disabled:opacity-50">
              <Check className="h-3.5 w-3.5" /> Mark as Closed
            </button>
          ) : null}
        </div>
      </div>

      <div className="mb-8"><DocumentExportActions config={exportConfig} testIdPrefix="inward-delivery-challan" /></div>

      {/* ── CHALLAN INFO ── */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[
            { label: 'Challan Number', value: doc.challan_number || '-' },
            { label: 'Challan Date', value: formatDate(doc.challan_date) },
            { label: 'Due Date', value: formatDate(doc.due_date) },
            { label: 'Vendor', value: doc.vendor_name || '-' },
            { label: 'Company (Receiving)', value: doc.company_name || '-' },
            { label: 'State of Supply', value: doc.state_of_supply || '-' },
          ].map(({ label, value }) => (
            <div key={label}>
              <span className="block text-xs font-medium text-slate-500 uppercase">{label}</span>
              <span className="text-sm font-semibold text-slate-900">{value}</span>
            </div>
          ))}
          {doc.description && (
            <div className="col-span-2">
              <span className="block text-xs font-medium text-slate-500 uppercase">Description</span>
              <span className="text-sm text-slate-900">{doc.description}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── QC CHECK SECTION ── */}
      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">QC Check</h2>
            <p className="text-xs text-slate-500 mt-0.5">Review the Item ID and Asset ID for each unit, then click "Mark QC" to set status and remark</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={autoGenAllItemIds} disabled={allItemIdsGenerated} data-testid="auto-gen-all-item-ids-btn"
              title={allItemIdsGenerated ? 'All Item IDs are already generated' : 'Generate all missing Item IDs'}
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-slate-300 text-slate-600 text-xs font-bold uppercase tracking-wider rounded-sm hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-600 disabled:hover:border-slate-300">
              <Wand2 className="h-3.5 w-3.5" /> Auto Generate Missing IDs
            </button>
          {qcDirty && (
            <button onClick={saveQC} disabled={savingQC} data-testid="save-qc-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50">
              {savingQC ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save QC Changes
            </button>
          )}
          </div>
        </div>

        {hasPassedUnits && !allUnitsQCCompleted && !billConverted && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 text-amber-700 text-sm" data-testid="qc-incomplete-message">
            Complete QC for every unit by marking each one Passed or Failed before converting this challan to a Purchase Bill.
          </div>
        )}

        <div className="divide-y divide-slate-100">
          {(doc.line_items || []).filter(li => li.product_id).map((li, liIdx) => {
            const productName = getName(products, li.product_id);
            const units_for_li = qcData[liIdx] || [];
            const passedCount = units_for_li.filter(u => u.qc_status === 'passed').length;
            const failedCount = units_for_li.filter(u => u.qc_status === 'failed').length;
            const prod = products.find(p => p.id === li.product_id);

            return (
              <div key={liIdx} className="p-6">
                {/* Product header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-base font-bold text-slate-900">{productName}</span>
                    <span className="text-xs text-slate-500">Qty received: {li.quantity}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold">
                    {passedCount > 0 && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">✓ {passedCount} Passed</span>}
                    {failedCount > 0 && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">✗ {failedCount} Failed</span>}
                    {passedCount === 0 && failedCount === 0 && <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded">Pending QC</span>}
                  </div>
                </div>

                {/* Unit rows */}
                <div className="space-y-2">
                  {units_for_li.map((unit, unitIdx) => (
                    <div key={unitIdx} data-testid={`qc-unit-${liIdx}-${unitIdx}`}
                      className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-sm border transition-colors ${
                        unit.qc_status === 'passed' ? 'border-green-200 bg-green-50' :
                        unit.qc_status === 'failed' ? 'border-red-200 bg-red-50' :
                        'border-slate-200 bg-slate-50'
                      }`}>
                      {/* Unit number */}
                      <span className="text-xs font-bold text-slate-500 w-12 shrink-0">Unit {unit.unit_number}</span>

                      {/* Item ID + Auto-gen */}
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <input
                          type="text"
                          value={unit.item_id}
                          onChange={e => updateQCUnit(liIdx, unitIdx, 'item_id', e.target.value.toUpperCase())}
                          placeholder="Enter or paste Item ID / serial number"
                          data-testid={`item-id-input-${liIdx}-${unitIdx}`}
                          className="flex-1 min-w-0 px-3 py-1.5 border border-slate-200 rounded-sm text-sm font-mono bg-white focus:border-orange-500 focus:outline-none"
                        />
                        <button
                          onClick={() => autoGenItemId(liIdx, unitIdx)}
                          disabled={Boolean(unit.item_id?.trim())}
                          title={unit.item_id?.trim() ? 'Item ID is already generated' : 'Auto-generate Item ID'}
                          data-testid={`auto-gen-btn-${liIdx}-${unitIdx}`}
                          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 text-slate-600 text-xs font-medium rounded-sm hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-600 disabled:hover:border-slate-300">
                          <Wand2 className="h-3 w-3" /> Auto
                        </button>
                      </div>

                      {/* Asset ID */}
                      <div className="flex flex-1 items-center gap-2 min-w-0">
                        <input
                          type="text"
                          value={unit.asset_id || ''}
                          onChange={e => updateQCUnit(liIdx, unitIdx, 'asset_id', e.target.value)}
                          placeholder="Enter Asset ID"
                          data-testid={`asset-id-input-${liIdx}-${unitIdx}`}
                          className="flex-1 min-w-0 px-3 py-1.5 border border-slate-200 rounded-sm text-sm font-mono bg-white focus:border-orange-500 focus:outline-none"
                        />
                      </div>

                      {/* Remark display (if set) */}
                      {unit.remark && (
                        <span className="text-xs text-slate-500 italic max-w-[180px] truncate shrink-0" title={unit.remark}>
                          "{unit.remark}"
                        </span>
                      )}

                      {/* Mark QC button + status badge + reset */}
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => openQCModal(liIdx, unitIdx)}
                          data-testid={`mark-qc-btn-${liIdx}-${unitIdx}`}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase rounded-sm border transition-colors ${
                            unit.qc_status === 'passed' ? 'border-green-400 text-green-700 hover:bg-green-50' :
                            unit.qc_status === 'failed' ? 'border-red-400 text-red-700 hover:bg-red-50' :
                            'border-slate-300 text-slate-600 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50'
                          }`}>
                          {unit.qc_status === 'passed' && <ShieldCheck className="h-3.5 w-3.5" />}
                          {unit.qc_status === 'failed' && <ShieldX className="h-3.5 w-3.5" />}
                          {unit.qc_status === 'pending' && <ShieldCheck className="h-3.5 w-3.5" />}
                          Mark QC
                        </button>
                        <span className={`shrink-0 px-2 py-0.5 text-xs font-bold uppercase rounded ${QC_STYLES[unit.qc_status]}`}>
                          {unit.qc_status}
                        </span>
                        {unit.qc_status !== 'pending' && (
                          <button onClick={() => resetQCUnit(liIdx, unitIdx)}
                            title="Reset QC" data-testid={`qc-reset-btn-${liIdx}-${unitIdx}`}
                            className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors">
                            <RotateCcw className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {qcDirty && (
          <div className="px-6 pb-4 flex items-center justify-end">
            <button onClick={saveQC} disabled={savingQC} data-testid="save-qc-btn-bottom"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-orange-500 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50">
              {savingQC ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save QC Changes
            </button>
          </div>
        )}
      </div>

      {/* ── LINE ITEMS SUMMARY ── */}
      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-right">Price/Unit</th>
                <th className="px-4 py-3 text-right">Disc %</th>
                <th className="px-4 py-3 text-left">Tax</th>
                <th className="px-4 py-3 text-right">Tax Amt</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-center">QC</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, idx) => {
                const liQC = qcData[idx] || [];
                const passed = liQC.filter(u => u.qc_status === 'passed').length;
                const failed = liQC.filter(u => u.qc_status === 'failed').length;
                const pending = liQC.filter(u => u.qc_status === 'pending').length;
                return (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{getName(products, li.product_id)}</td>
                    <td className="px-4 py-3 text-right">{li.quantity}</td>
                    <td className="px-4 py-3">{getName(units, li.unit_id)}</td>
                    <td className="px-4 py-3 text-right">{(li.price_per_unit || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">{li.discount_percentage || 0}%</td>
                    <td className="px-4 py-3">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                    <td className="px-4 py-3 text-right">{(li.tax_amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-medium">{(li.total_amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1 text-xs font-semibold">
                        {passed > 0 && <span className="text-green-600">✓{passed}</span>}
                        {failed > 0 && <span className="text-red-600">✗{failed}</span>}
                        {pending > 0 && <span className="text-slate-400">?{pending}</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── TOTALS ── */}
      <div className="bg-white rounded-sm border border-slate-200 p-6">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Subtotal</span>
            <span className="font-medium">₹{(doc.subtotal || 0).toFixed(2)}</span>
          </div>
          {doc.round_off && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Round Off</span>
              <span>{(doc.round_off_amount || 0) >= 0 ? '+' : ''}{(doc.round_off_amount || 0).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2">
            <span>Total</span>
            <span>₹{(doc.total || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── QC MODAL ── */}
      {qcModal && (() => {
        const unit = qcData[qcModal.liIdx]?.[qcModal.unitIdx];
        const li = doc.line_items?.[qcModal.liIdx];
        const productName = getName(products, li?.product_id);
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-sm w-full max-w-md">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Mark QC</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{productName} — Unit {unit?.unit_number} &nbsp;|&nbsp; <span className="font-mono">{unit?.item_id}</span></p>
                </div>
                <button onClick={() => setQcModal(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                {/* Pass / Fail */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">QC Status <span className="text-red-500">*</span></label>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setQcModalStatus('passed')} data-testid="qc-modal-pass"
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 border rounded-sm text-sm font-bold uppercase transition-colors ${
                        qcModalStatus === 'passed' ? 'bg-green-600 border-green-600 text-white' : 'border-green-400 text-green-600 hover:bg-green-50'
                      }`}>
                      <ShieldCheck className="h-4 w-4" /> Pass
                    </button>
                    <button type="button" onClick={() => setQcModalStatus('failed')} data-testid="qc-modal-fail"
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 border rounded-sm text-sm font-bold uppercase transition-colors ${
                        qcModalStatus === 'failed' ? 'bg-red-600 border-red-600 text-white' : 'border-red-400 text-red-600 hover:bg-red-50'
                      }`}>
                      <ShieldX className="h-4 w-4" /> Fail
                    </button>
                  </div>
                </div>

                {/* Remark */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Remark {qcModalStatus === 'failed' && <span className="text-red-500">*</span>}
                    {qcModalStatus === 'passed' && <span className="text-slate-400 font-normal ml-1">(optional)</span>}
                  </label>
                  <textarea
                    value={qcModalRemark}
                    onChange={e => { setQcModalRemark(e.target.value); setQcModalError(''); }}
                    placeholder={qcModalStatus === 'failed' ? 'Describe the defect or reason for failure…' : 'Optional note…'}
                    rows={3}
                    data-testid="qc-modal-remark"
                    className={`w-full px-3 py-2 border rounded-sm text-sm focus:outline-none resize-none ${
                      qcModalError ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-orange-500'
                    }`}
                  />
                  {qcModalError && <p className="text-xs text-red-600 mt-1">{qcModalError}</p>}
                </div>
              </div>
              <div className="flex gap-3 justify-end px-6 pb-5">
                <button onClick={() => setQcModal(null)}
                  className="px-5 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 font-medium">
                  Cancel
                </button>
                <button onClick={saveQCModal} data-testid="qc-modal-save"
                  className={`px-6 py-2 text-white font-bold uppercase tracking-wider rounded-sm transition-colors ${
                    qcModalStatus === 'passed' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}>
                  Save
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── MARK CLOSED CONFIRM MODAL ── */}
      {showStatusConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Mark as Closed?</h3>
            <p className="text-slate-600 mb-6">This will close the inward delivery challan. You can reopen it later.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowStatusConfirm(false)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              <button onClick={handleMarkClosed} disabled={updatingStatus} data-testid="confirm-close-btn"
                className="px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2">
                {updatingStatus && <Loader2 className="h-4 w-4 animate-spin" />} Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RETURN FAILED UNITS MODAL ── */}
      {showReturnModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div>
                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Return Failed Units</h2>
                <p className="text-xs text-slate-500 mt-0.5">The following failed QC units will be added to a Return Inward DC</p>
              </div>
              <button onClick={() => setShowReturnModal(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-sm mb-5 text-sm">
                <RotateCcw className="h-4 w-4 shrink-0" />
                Creating Return Inward DC from <strong className="ml-1">{doc.challan_number}</strong>. A new Return document will be created.
              </div>

              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                      <th className="px-3 py-3 text-left">Product</th>
                      <th className="px-3 py-3 text-center">Unit #</th>
                      <th className="px-3 py-3 text-left">Item ID</th>
                      <th className="px-3 py-3 text-left">Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returningUnits.map((u, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="px-3 py-3 font-medium text-slate-900">{u.product_name}</td>
                        <td className="px-3 py-3 text-center text-slate-600">{u.unit_number}</td>
                        <td className="px-3 py-3 font-mono text-xs text-slate-700">{u.item_id || '-'}</td>
                        <td className="px-3 py-3 text-slate-600 text-xs italic">{u.remark || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowReturnModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button onClick={handleCreateReturn} disabled={creatingReturn} data-testid="confirm-return-btn"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors">
                  {creatingReturn ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  Confirm Return
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CONVERT TO PURCHASE BILL MODAL ── */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-4xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div>
                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Convert to Purchase Bill</h2>
                <p className="text-xs text-slate-500 mt-0.5">Only QC Passed items are shown. Review & adjust before converting.</p>
              </div>
              <button onClick={() => setShowConvertModal(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-sm mb-6 text-sm">
                <FileText className="h-4 w-4 shrink-0" />
                Converting from Inward Challan <strong className="ml-1">{doc.challan_number || ''}</strong>. Vendor and company will be pre-filled automatically.
              </div>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                      <th className="px-3 py-3 text-left">Product</th>
                      <th className="px-3 py-3 text-right">QC Passed Qty</th>
                      <th className="px-3 py-3 text-right">Price/Unit</th>
                      <th className="px-3 py-3 text-right">Disc %</th>
                      <th className="px-3 py-3 text-right">Disc Amt</th>
                      <th className="px-3 py-3 text-left">Tax</th>
                      <th className="px-3 py-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalItems.map((item, idx) => (
                      <tr key={item.key} className="border-b border-slate-100">
                        <td className="px-3 py-3 font-medium text-slate-900">{getName(products, item.product_id)}</td>
                        <td className="px-3 py-3 text-right font-medium text-green-700">{item.quantity}</td>
                        <td className="px-3 py-2">
                          <input type="number" value={item.price_per_unit} step="0.01"
                            onChange={e => updateModalItem(idx, 'price_per_unit', e.target.value)}
                            data-testid={`modal-price-${idx}`}
                            className="w-28 px-2 py-1 border border-slate-200 rounded-sm text-right focus:border-orange-500 focus:outline-none text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" value={item.discount_percentage} min="0" max="100" step="0.01"
                            onChange={e => updateModalItem(idx, 'discount_percentage', e.target.value)}
                            data-testid={`modal-disc-${idx}`}
                            className="w-20 px-2 py-1 border border-slate-200 rounded-sm text-right focus:border-orange-500 focus:outline-none text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" value={item.discount_amount} min="0" step="0.01"
                            onChange={e => updateModalItem(idx, 'discount_amount', e.target.value)}
                            data-testid={`modal-discount-amount-${idx}`}
                            className="w-24 px-2 py-1 border border-slate-200 rounded-sm text-right focus:border-orange-500 focus:outline-none text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <select value={item.tax_id} onChange={e => updateModalItem(idx, 'tax_id', e.target.value)}
                            data-testid={`modal-tax-${idx}`}
                            className="w-36 px-2 py-1 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                            <option value="">No Tax</option>
                            {taxes.map(t => <option key={t.id} value={t.id}>{t.name} ({t.percentage}%)</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-3 text-right font-medium">{item.total_amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 font-bold text-sm">
                      <td colSpan={6} className="px-3 py-3 text-right text-slate-700">Total</td>
                      <td className="px-3 py-3 text-right text-slate-900">₹{modalSubtotal.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowConvertModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button onClick={handleConvert} data-testid="confirm-convert-btn"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors">
                  <FileText className="h-4 w-4" /> Go to Create Purchase Bill
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
